import { useTranslation } from 'react-i18next';

import { LOCAL_TOOL_DEFINITIONS } from '@/components/atoms/Editors/BlockNoteEditor/localToolTypes';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ListTooltipContent } from '@/components/atoms/ListTooltipContent/ListTooltipContent';
import { useEffectiveTools } from '@/hooks/useEffectiveTools';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { AIAgent } from '@/shared/types/brain';

const TOOL_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  LOCAL_TOOL_DEFINITIONS.map(({ tool, labelKey }) => [tool, labelKey])
);

const TOOL_NAME_CLASSES = 'min-w-0 truncate text-sm leading-5 font-normal';

interface ToolsCellProps {
  agent: AIAgent;
}

export const ToolsCell = ({ agent }: ToolsCellProps) => {
  const { t } = useTranslation();
  const { effectiveTools: tools } = useEffectiveTools(agent);
  const labels = tools.map((tool) => (TOOL_LABEL_KEYS[tool] ? t(TOOL_LABEL_KEYS[tool]) : tool));
  const [first, ...rest] = labels;
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [first]);

  if (labels.length === 0) {
    return <span className="text-foreground">-</span>;
  }

  if (rest.length === 0) {
    return (
      <InfoTooltip content={isTruncated ? first : ''} size="auto">
        <span ref={ref} className={`${TOOL_NAME_CLASSES} block text-foreground`}>
          {first}
        </span>
      </InfoTooltip>
    );
  }

  return (
    <InfoTooltip
      variant="rich"
      size="auto"
      side="right"
      align="center"
      content={
        <ListTooltipContent
          title={t('brains.tools_tooltip')}
          items={labels.map((label, index) => ({ id: `${tools[index] ?? index}`, label }))}
        />
      }
    >
      <span className="flex max-w-full min-w-0 items-center gap-1 text-left text-foreground">
        <span className={TOOL_NAME_CLASSES}>{first}</span>
        <span className="shrink-0 text-sm leading-5">+{rest.length}</span>
      </span>
    </InfoTooltip>
  );
};
